/**
 * Customer Registration with Backend API
 * Handles customer registration via custom backend using Shopify Admin API
 */

(function () {
  'use strict';

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    const form = document.getElementById('customer-registration-form');
    if (!form) return;

    const phoneInput = document.getElementById('registration-phone');

    form.addEventListener('submit', handleFormSubmit);

    // Setup phone input formatting
    if (phoneInput) {
      phoneInput.addEventListener('input', formatPhoneNumber);
    }

    // Setup tooltip click handlers
    setupTooltipHandlers();

    // Close popover when Sign Up button is clicked to open the modal
    setupPopoverClose();
  }

  function setupTooltipHandlers() {
    // Handle tooltip click for mobile/accessibility
    const tooltipIcons = document.querySelectorAll('.tooltip-icon');

    tooltipIcons.forEach((icon) => {
      icon.addEventListener('click', (e) => {
        e.preventDefault();
        icon.classList.toggle('tooltip-active');

        // Close other active tooltips
        tooltipIcons.forEach((otherIcon) => {
          if (otherIcon !== icon) {
            otherIcon.classList.remove('tooltip-active');
          }
        });
      });

      // Close tooltip when clicking outside
      document.addEventListener('click', (e) => {
        if (!icon.contains(e.target)) {
          icon.classList.remove('tooltip-active');
        }
      });

      // Handle keyboard navigation
      icon.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          icon.click();
        }
      });
    });
  }

  function setupPopoverClose() {
    // Find all buttons that control the registration modal
    const signUpButtons = document.querySelectorAll('[aria-controls="registration-modal"]');

    signUpButtons.forEach((button) => {
      button.addEventListener('click', () => {
        // Find the parent popover element
        const popover = button.closest('x-popover');
        if (popover && typeof popover.hide === 'function') {
          popover.hide();
        }
      });
    });
  }

  async function handleFormSubmit(event) {
    event.preventDefault();

    const form = event.target;
    const submitButton = document.getElementById('registration-submit');
    const formData = new FormData(form);

    // Clear previous errors
    clearErrors();
    hideMessages();

    // Validate form
    const validation = validateForm(formData);
    if (!validation.isValid) {
      showErrors(validation.errors);
      return;
    }

    // Show loading state
    setLoading(submitButton, true);

    try {
      // Create customer via backend API
      const result = await createCustomer(formData);

      console.log('RESULT', result);

      if (result.success) {
        showSuccess(result.message || 'Account created successfully! Redirecting to login...');

        // Close modal if present
        const modal = document.getElementById('registration-modal');
        if (modal) {
          setTimeout(() => {
            modal.hide();
          }, 1500);
        }

        // Redirect to account login after 2 seconds with return_to parameter
        setTimeout(() => {
          const returnTo = encodeURIComponent(window.Shopify?.routes?.customer_account_page_url || '/account');
          const locale = window.Shopify?.locale || 'en';
          const loginUrl = `/customer_authentication/login?return_to=${returnTo}&locale=${locale}`;
          window.location.href = loginUrl;
        }, 2000);
      } else {
        // Check if it's an "email already exists" error
        if (isEmailAlreadyExistsError(result.errors)) {
          showActivateAccountModal();
        } else {
          showErrors(result.errors);
        }
      }
    } catch (error) {
      console.error('Registration error:', error);
      showGlobalError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(submitButton, false);
    }
  }

  function isEmailAlreadyExistsError(errors) {
    if (!errors?.email) return false;
    const emailError = errors.email.toLowerCase();
    return emailError.includes('already') || emailError.includes('taken');
  }

  function showActivateAccountModal() {
    const registrationModal = document.getElementById('registration-modal');
    const activateModal = document.getElementById('activate-account-modal');

    if (registrationModal && typeof registrationModal.hide === 'function') {
      registrationModal.hide();
    }

    if (activateModal && typeof activateModal.show === 'function') {
      activateModal.show();
    }
  }

  async function createCustomer(formData) {
    const firstName = formData.get('firstName')?.trim();
    const lastName = formData.get('lastName')?.trim();
    const email = formData.get('email')?.trim();
    const phone = formData.get('phone')?.trim();
    const dateOfBirth = formData.get('dateOfBirth')?.trim();
    const acceptsMarketing = formData.get('acceptsMarketing') === 'on';

    // Get backend configuration
    const backendUrl = window.Shopify?.backendUrl;
    const storeIdentifier = window.Shopify?.storeIdentifier || 'pilot-coffee';

    if (!backendUrl) {
      console.error('Backend URL not configured');
      return {
        success: false,
        errors: { _global: 'Configuration error. Please contact support.' },
      };
    }

    const payload = {
      firstName,
      lastName,
      email,
      phone,
      acceptsMarketing,
      dateOfBirth: dateOfBirth || null,
    };

    try {
      const response = await fetch(`${backendUrl}/api/customers/register?store=${storeIdentifier}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      // Backend returns { success: boolean, errors?: object, customer?: object, message?: string }
      if (data.success) {
        return {
          success: true,
          customer: data.customer,
          message: data.message,
        };
      } else {
        return {
          success: false,
          errors: data.errors || { _global: 'Failed to create account. Please try again.' },
        };
      }
    } catch (error) {
      console.error('API error:', error);
      return {
        success: false,
        errors: { _global: 'Network error. Please check your connection and try again.' },
      };
    }
  }

  function validateForm(formData) {
    const errors = {};

    const firstName = formData.get('firstName')?.trim();
    const lastName = formData.get('lastName')?.trim();
    const email = formData.get('email')?.trim();
    const phone = formData.get('phone')?.trim();
    const acceptsTerms = formData.get('acceptsTerms');

    if (!firstName) {
      errors.firstName = 'First name is required';
    }

    if (!lastName) {
      errors.lastName = 'Last name is required';
    }

    if (!email) {
      errors.email = 'Email is required';
    } else if (!isValidEmail(email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!phone) {
      errors.phone = 'Phone number is required';
    } else if (!isValidPhone(phone)) {
      errors.phone = 'Please enter a valid phone number with country code (e.g., +1 555-123-4567)';
    }

    if (!acceptsTerms) {
      errors.acceptsTerms = 'You must agree to the Terms and Conditions to create an account';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  }

  function isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  function isValidPhone(phone) {
    // Basic validation: must start with + and contain at least 10 digits
    const phoneRegex = /^\+[\d\s\-()]{10,}$/;
    return phoneRegex.test(phone);
  }

  function showErrors(errors) {
    Object.keys(errors).forEach((field) => {
      if (field === '_global') {
        showGlobalError(errors[field]);
      } else {
        const errorDiv = document.getElementById(`error-${field}`);
        if (errorDiv) {
          errorDiv.textContent = errors[field];
          errorDiv.style.display = 'block';

          // Add error class to the form control
          const formControl = errorDiv.previousElementSibling;
          if (formControl && formControl.classList.contains('form-control')) {
            formControl.classList.add('has-error');
          }
        }
      }
    });
  }

  function clearErrors() {
    document.querySelectorAll('.form-error').forEach((error) => {
      error.textContent = '';
      error.style.display = 'none';

      // Remove error class from the form control
      const formControl = error.previousElementSibling;
      if (formControl && formControl.classList.contains('form-control')) {
        formControl.classList.remove('has-error');
      }
    });
  }

  function showGlobalError(message) {
    const errorBanner = document.getElementById('registration-error');
    const errorMessage = document.getElementById('registration-error-message');
    if (errorBanner && errorMessage) {
      errorMessage.textContent = message;
      errorBanner.style.display = 'block';
    }
  }

  function showSuccess(message) {
    const successBanner = document.getElementById('registration-success');
    const successMessage = document.getElementById('registration-success-message');
    if (successBanner && successMessage) {
      successMessage.textContent = message;
      successBanner.style.display = 'block';
    }
  }

  function hideMessages() {
    const errorBanner = document.getElementById('registration-error');
    const successBanner = document.getElementById('registration-success');
    if (errorBanner) errorBanner.style.display = 'none';
    if (successBanner) successBanner.style.display = 'none';
  }

  function setLoading(button, isLoading) {
    if (!button) return;

    const label = button.querySelector('.button-label');
    const spinner = button.querySelector('.button-spinner');

    if (isLoading) {
      button.classList.add('is-loading');
      button.disabled = true;
      if (spinner) spinner.style.display = 'inline-block';
    } else {
      button.classList.remove('is-loading');
      button.disabled = false;
      if (spinner) spinner.style.display = 'none';
    }
  }

  function formatPhoneNumber(event) {
    const input = event.target;
    const hiddenInput = document.getElementById('registration-phone-full');

    // Get only digits from the input
    let value = input.value.replace(/\D/g, '');

    // Limit to 10 digits
    value = value.substring(0, 10);

    // Format as (XXX) XXX-XXXX
    let formatted = '';
    if (value.length > 0) {
      formatted = '(' + value.substring(0, 3);
    }
    if (value.length >= 4) {
      formatted += ') ' + value.substring(3, 6);
    }
    if (value.length >= 7) {
      formatted += '-' + value.substring(6, 10);
    }

    // Update visible input
    input.value = formatted;

    // Update hidden input with full format +1XXXXXXXXXX
    if (hiddenInput && value.length === 10) {
      hiddenInput.value = '+1' + value;
    } else if (hiddenInput) {
      hiddenInput.value = '';
    }
  }
})();
